import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, ChevronRight, ChevronDown, Edit, Trash2, GripVertical } from "lucide-react";
import { Label } from "@/components/ui/label";
import { slugify } from "@/lib/utils";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export type FormFieldOption = {
  id: string;
  field_id: string;
  valor: string;
  label: string;
  ordem: number;
  parent_option_id: string | null;
  sub_opcoes?: FormFieldOption[];
  tenant_id: string;
  created_at?: string;
  valor_produto?: number | null;
};

interface HierarchicalOptionsEditorProps {
  fieldId: string;
  options: FormFieldOption[];
  onOptionsChange: (options: FormFieldOption[]) => void;
  allowSubOptions?: boolean;
  showProductPrice?: boolean;
}

interface FlatNode {
  option: FormFieldOption;
  level: number;
  isLast: boolean;
  parentIsLast: boolean[];
  index: number;
}

const INDENT = 24;
const ICON_SIZE = 16;

interface SortableOptionItemProps {
  id: string;
  children: (attributes: any, listeners: any) => React.ReactNode;
}

function SortableOptionItem({ id, children }: SortableOptionItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children(attributes, listeners)}
    </div>
  );
}

export function HierarchicalOptionsEditor({
  fieldId,
  options,
  onOptionsChange,
  allowSubOptions = false,
  showProductPrice = false,
}: HierarchicalOptionsEditorProps) {
  const [expandedOptions, setExpandedOptions] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newOptionValue, setNewOptionValue] = useState("");
  const [newOptionLabel, setNewOptionLabel] = useState("");
  const [newOptionProductPrice, setNewOptionProductPrice] = useState<number | null>(null);
  const [addingToParent, setAddingToParent] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const toggleExpand = (optionId: string) => {
    const newExpanded = new Set(expandedOptions);
    if (newExpanded.has(optionId)) {
      newExpanded.delete(optionId);
    } else {
      newExpanded.add(optionId);
    }
    setExpandedOptions(newExpanded);
  };

  const addOption = (parentId: string | null = null) => {
    if (!newOptionLabel.trim()) return;

    const newOption: FormFieldOption = {
      id: `temp-${Date.now()}-${Math.random()}`,
      field_id: fieldId,
      valor: slugify(newOptionLabel.trim()),
      label: newOptionLabel.trim(),
      ordem: options.filter(o => o.parent_option_id === parentId).length,
      parent_option_id: parentId,
      sub_opcoes: [],
      tenant_id: "",
      valor_produto: newOptionProductPrice ?? null,
    };

    const updatedOptions = parentId
      ? addSubOption(options, parentId, newOption)
      : [...options, newOption];

    onOptionsChange(updatedOptions);
    setNewOptionValue("");
    setNewOptionLabel("");
    setNewOptionProductPrice(null);
    setAddingToParent(null);
  };

  const addSubOption = (
    opts: FormFieldOption[],
    parentId: string,
    newOption: FormFieldOption
  ): FormFieldOption[] => {
    return opts.map(opt => {
      if (opt.id === parentId) {
        return {
          ...opt,
          sub_opcoes: [...(opt.sub_opcoes || []), newOption],
        };
      }
      if (opt.sub_opcoes) {
        return {
          ...opt,
          sub_opcoes: addSubOption(opt.sub_opcoes, parentId, newOption),
        };
      }
      return opt;
    });
  };

  const deleteOption = (optionId: string) => {
    const updatedOptions = removeOption(options, optionId);
    onOptionsChange(updatedOptions);
  };

  const removeOption = (opts: FormFieldOption[], idToRemove: string): FormFieldOption[] => {
    return opts
      .filter(opt => opt.id !== idToRemove)
      .map(opt => ({
        ...opt,
        sub_opcoes: opt.sub_opcoes ? removeOption(opt.sub_opcoes, idToRemove) : undefined,
      }));
  };

  const updateOption = (optionId: string, updates: Partial<FormFieldOption>) => {
    const updatedOptions = updateOptionInTree(options, optionId, updates);
    onOptionsChange(updatedOptions);
    setEditingId(null);
    setNewOptionValue("");
    setNewOptionLabel("");
    setNewOptionProductPrice(null);
  };

  // Função para reordenar opções do mesmo nível
  const reorderOptions = (
    opts: FormFieldOption[],
    activeId: string,
    overId: string
  ): FormFieldOption[] => {
    // Encontrar as opções ativas
    let activeOption: FormFieldOption | null = null;
    let activeParentId: string | null = null;
    let activeIndex = -1;

    const findOption = (optsList: FormFieldOption[], parentId: string | null = null): boolean => {
      for (let i = 0; i < optsList.length; i++) {
        if (optsList[i].id === activeId) {
          activeOption = optsList[i];
          activeParentId = parentId;
          activeIndex = i;
          return true;
        }
        if (optsList[i].sub_opcoes && findOption(optsList[i].sub_opcoes, optsList[i].id)) {
          return true;
        }
      }
      return false;
    };

    findOption(opts);

    if (!activeOption || activeIndex === -1) return opts;

    // Encontrar as opções do mesmo nível (mesmo parent)
    const findSiblings = (optsList: FormFieldOption[], parentId: string | null = null): FormFieldOption[] | null => {
      if (parentId === activeParentId) {
        return optsList;
      }
      for (const opt of optsList) {
        if (opt.sub_opcoes) {
          const siblings = findSiblings(opt.sub_opcoes, opt.id);
          if (siblings) return siblings;
        }
      }
      return null;
    };

    const siblings = activeParentId === null 
      ? opts 
      : (() => {
          const findParent = (optsList: FormFieldOption[]): FormFieldOption[] | null => {
            for (const opt of optsList) {
              if (opt.id === activeParentId) {
                return opt.sub_opcoes || [];
              }
              if (opt.sub_opcoes) {
                const result = findParent(opt.sub_opcoes);
                if (result) return result;
              }
            }
            return null;
          };
          return findParent(opts);
        })();

    if (!siblings) return opts;

    const overIndex = siblings.findIndex(opt => opt.id === overId);
    if (overIndex === -1) return opts;

    // Reordenar apenas se for do mesmo nível
    const reorderedSiblings = arrayMove(siblings, activeIndex, overIndex);
    
    // Atualizar a ordem
    const updatedSiblings = reorderedSiblings.map((opt, index) => ({
      ...opt,
      ordem: index,
    }));

    // Atualizar a árvore
    if (activeParentId === null) {
      return updatedSiblings;
    } else {
      const updateTree = (optsList: FormFieldOption[]): FormFieldOption[] => {
        return optsList.map(opt => {
          if (opt.id === activeParentId) {
            return {
              ...opt,
              sub_opcoes: updatedSiblings,
            };
          }
          if (opt.sub_opcoes) {
            return {
              ...opt,
              sub_opcoes: updateTree(opt.sub_opcoes),
            };
          }
          return opt;
        });
      };
      return updateTree(opts);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const updatedOptions = reorderOptions(options, active.id as string, over.id as string);
      onOptionsChange(updatedOptions);
    }
  };

  const updateOptionInTree = (
    opts: FormFieldOption[],
    id: string,
    updates: Partial<FormFieldOption>
  ): FormFieldOption[] => {
    return opts.map(opt => {
      if (opt.id === id) {
        return { ...opt, ...updates };
      }
      if (opt.sub_opcoes) {
        return {
          ...opt,
          sub_opcoes: updateOptionInTree(opt.sub_opcoes, id, updates),
        };
      }
      return opt;
    });
  };

  const flattenTree = (
    opts: FormFieldOption[],
    level: number = 0,
    parentIsLast: boolean[] = [],
    startIndex: number = 0
  ): FlatNode[] => {
    const result: FlatNode[] = [];
    
    opts.forEach((opt, index) => {
      const isLast = index === opts.length - 1;
      const nodeIndex = startIndex + result.length;
      
      result.push({
        option: opt,
        level,
        isLast,
        parentIsLast,
        index: nodeIndex,
      });
      
      if (opt.sub_opcoes && opt.sub_opcoes.length > 0 && expandedOptions.has(opt.id)) {
        result.push(...flattenTree(opt.sub_opcoes, level + 1, [...parentIsLast, isLast], nodeIndex + 1));
      }
    });
    
    return result;
  };

  const renderItem = (node: FlatNode, allNodes: FlatNode[], dragAttributes?: any, dragListeners?: any) => {
    const { option, level, isLast, parentIsLast } = node;
    const hasChildren = !!(option.sub_opcoes && option.sub_opcoes.length > 0);
    const isExpanded = expandedOptions.has(option.id);
    const isEditing = editingId === option.id;
    const isAddingSubOption = addingToParent === option.id;

    // Calcular quais ancestrais precisam de linha vertical
    const needsVerticalLine = parentIsLast.map((isParentLast, ancestorLevel) => {
      if (isParentLast) return false;
      // Verificar se há nós abaixo que precisam desta linha
      for (let i = node.index + 1; i < allNodes.length; i++) {
        const nextNode = allNodes[i];
        if (nextNode.level <= ancestorLevel) break;
        if (nextNode.level > ancestorLevel) return true;
      }
      return false;
    });

    return (
      <div>
        {/* Item principal */}
        <div 
          className="group flex items-center hover:bg-muted/50 transition-colors py-2 pl-2"
        >
          {/* Área de indentação com linhas da árvore */}
          <div className="flex" style={{ width: `${level * INDENT}px`, flexShrink: 0 }}>
            {Array.from({ length: level }).map((_, ancestorLevel) => (
              <div
                key={ancestorLevel}
                style={{
                  width: `${INDENT}px`,
                  display: 'flex',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                {needsVerticalLine[ancestorLevel] ? (
                  <div
                    style={{
                      width: '1px',
                      backgroundColor: 'hsl(var(--muted-foreground) / 0.15)',
                      minHeight: '100%'
                    }}
                  />
                ) : (
                  <div style={{ width: '1px' }} />
                )}
              </div>
            ))}
          </div>

          {/* Drag handle */}
          {dragAttributes && dragListeners && (
            <div
              {...dragAttributes}
              {...dragListeners}
              className="cursor-grab active:cursor-grabbing mr-2 flex items-center"
              style={{ flexShrink: 0 }}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
          )}

          {/* Container do ícone com linha horizontal */}
          <div 
            className="flex items-center justify-center"
            style={{ 
              width: `${ICON_SIZE}px`, 
              height: `${ICON_SIZE}px`,
              marginRight: '0px',
              marginLeft: allowSubOptions ? '4px' : '0',
              flexShrink: 0
            }}
          >
            {/* Linha horizontal conectando ao pai */}
            {level > 0 && (
              <div
                style={{
                  width: `${(INDENT - ICON_SIZE) / 2}px`,
                  height: '1px',
                  backgroundColor: 'hsl(var(--muted-foreground) / 0.15)',
                  marginRight: '2px',
                  flexShrink: 0
                }}
              />
            )}
            
            {allowSubOptions && hasChildren ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleExpand(option.id);
                }}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            ) : (
              <div style={{ width: `${ICON_SIZE}px`, height: `${ICON_SIZE}px` }} />
            )}
          </div>

          {/* Conteúdo do item */}
          <div className="flex items-center gap-0 flex-1 min-w-0">
            {isEditing ? (
              <div className="flex-1 flex gap-2 items-center">
                <div className="flex-1 min-w-0">
                  <div className="flex gap-2 items-center">
                    <div className="flex-1 flex items-center gap-2 -ml-2">
                      <Input
                        value={newOptionLabel}
                        onChange={(e) => {
                          setNewOptionLabel(e.target.value);
                          setNewOptionValue(slugify(e.target.value));
                        }}
                        placeholder="Label"
                        className="h-9"
                      />
                      <div className="text-xs text-muted-foreground whitespace-nowrap">
                        Identificador: {newOptionValue || slugify(newOptionLabel) || "será gerado automaticamente"}
                      </div>
                    </div>
                    {showProductPrice && (
                      <div className="flex items-center gap-2 ml-6">
                        <Label className="text-xs whitespace-nowrap">Valor do Produto:</Label>
                        <MoneyInput
                          value={newOptionProductPrice ?? 0}
                          onValueChange={(value) => setNewOptionProductPrice(value)}
                          placeholder="0,00"
                          className="h-9 w-28"
                        />
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    updateOption(option.id, {
                      valor: newOptionValue || slugify(newOptionLabel),
                      label: newOptionLabel,
                      valor_produto: newOptionProductPrice ?? null,
                    });
                  }}
                >
                  Salvar
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mr-2"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setEditingId(null);
                    setNewOptionValue("");
                    setNewOptionLabel("");
                    setNewOptionProductPrice(null);
                  }}
                >
                  Cancelar
                </Button>
              </div>
            ) : (
              <>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-4">
                    <div className="font-medium text-sm">{option.label}</div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      Identificador: {option.valor}
                    </span>
                    {showProductPrice && option.valor_produto !== null && option.valor_produto !== undefined && (
                      <span className="text-xs text-primary font-medium whitespace-nowrap">
                        Valor: {new Intl.NumberFormat("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }).format(option.valor_produto)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditingId(option.id);
                      setNewOptionLabel(option.label);
                      setNewOptionValue(slugify(option.label));
                      setNewOptionProductPrice(option.valor_produto ?? null);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  {allowSubOptions && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setAddingToParent(option.id);
                        setNewOptionValue("");
                        setNewOptionLabel("");
                      }}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 mr-2 text-destructive hover:text-destructive"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      deleteOption(option.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Formulário para adicionar sub-opção - renderizado como item separado */}
        {isAddingSubOption && (
          <div 
            className="bg-muted/30 py-3 px-4"
            style={{ paddingLeft: `${(level + 1) * INDENT + ICON_SIZE + 8}px` }}
          >
            <div className="space-y-2">
              <Label className="text-sm">Adicionar Sub-opção</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <div className="flex gap-2 items-center">
                    <div className="flex-1 flex items-center gap-2">
                      <Input
                        value={newOptionLabel}
                        onChange={(e) => {
                          setNewOptionLabel(e.target.value);
                          setNewOptionValue(slugify(e.target.value));
                        }}
                        placeholder="Label da sub-opção"
                        className="h-9"
                      />
                      <div className="text-xs text-muted-foreground whitespace-nowrap">
                        Identificador: {newOptionValue || slugify(newOptionLabel) || "será gerado automaticamente"}
                      </div>
                    </div>
                    {showProductPrice && (
                      <div className="flex items-center gap-2 ml-6">
                        <Label className="text-xs whitespace-nowrap">Valor do Produto:</Label>
                        <MoneyInput
                          value={newOptionProductPrice ?? 0}
                          onValueChange={(value) => setNewOptionProductPrice(value)}
                          placeholder="0,00"
                          className="h-9 w-28"
                        />
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    addOption(option.id);
                  }}
                  disabled={!newOptionLabel.trim()}
                >
                  Adicionar
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAddingToParent(null);
                    setNewOptionValue("");
                    setNewOptionLabel("");
                    setNewOptionProductPrice(null);
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const rootOptions = options.filter(opt => !opt.parent_option_id);
  const flatNodes = flattenTree(rootOptions);

  return (
    <div className="space-y-4">
      <div className="border rounded-lg bg-card overflow-hidden">
        {flatNodes.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-12">
            Nenhuma opção cadastrada. Adicione uma opção abaixo.
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={flatNodes.map(node => node.option.id)}
              strategy={verticalListSortingStrategy}
            >
              <div>
                {flatNodes.map((node) => (
                  <SortableOptionItem key={node.option.id} id={node.option.id}>
                    {(attributes, listeners) => (
                      <div className="border-b border-border last:border-b-0">
                        {renderItem(node, flatNodes, attributes, listeners)}
                      </div>
                    )}
                  </SortableOptionItem>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Formulário para adicionar nova opção raiz */}
      {addingToParent === null && (
        <Card>
          <CardContent className="p-4">
            <div className="space-y-2">
              <Label>Adicionar Nova Opção</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <div className="flex gap-2 items-center">
                    <div className="flex-1 flex items-center gap-2">
                      <Input
                        value={newOptionLabel}
                        onChange={(e) => {
                          setNewOptionLabel(e.target.value);
                          setNewOptionValue(slugify(e.target.value));
                        }}
                        placeholder="Label da opção"
                        className="h-9"
                      />
                      <div className="text-xs text-muted-foreground whitespace-nowrap">
                        Identificador: {newOptionValue || slugify(newOptionLabel) || "será gerado automaticamente"}
                      </div>
                    </div>
                    {showProductPrice && (
                      <div className="flex items-center gap-2 ml-6">
                        <Label className="text-xs whitespace-nowrap">Valor do Produto:</Label>
                        <MoneyInput
                          value={newOptionProductPrice ?? 0}
                          onValueChange={(value) => setNewOptionProductPrice(value)}
                          placeholder="0,00"
                          className="h-9 w-28"
                        />
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    addOption();
                  }}
                  disabled={!newOptionLabel.trim()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
